import type { RouteObject } from "react-router-dom";

import {
  AuthenticatedLayoutV2Route,
  AuthenticatedV2Route,
  HomeV2Route,
  homeV2ClientLoader,
  LoginCallbackV2Route,
  LoginV2Route,
  NotFoundV2Route,
  SignUpV2Route,
  TwoFactorV2Route,
} from "@src/features/auth/v2";
import { CareProtocolOpportunitiesV2Route } from "@src/features/care-protocol-opportunities/v2";
import { VisitsV2Route, VisitV2Route } from "@src/features/encounters/v2";
import { HomeV2Route as HomePageV2Route } from "@src/features/home/v2";
import {
  PatientDetailV2Route,
  PatientsV2Route,
} from "@src/features/patients/v2";
import {
  AccountSettingsV2Route,
  PracticeInfoSettingsV2Route,
  PreferencesSettingsV2Route,
  SecuritySettingsV2Route,
  SettingsIndexV2Route,
  SettingsLayoutV2Route,
  TeamSettingsV2Route,
} from "@src/features/settings/v2";

/**
 * The promoted v2 route tree, shared between the web app (createBrowserRouter)
 * and the chrome extension (createMemoryRouter) so both mount the same routes
 * under RootV2Route. Keep this in sync in one place instead of two.
 */
export function createV2Routes(): RouteObject[] {
  return [
    {
      index: true,
      element: <HomeV2Route />,
      loader: homeV2ClientLoader,
    },
    {
      path: "login",
      element: <LoginV2Route />,
    },
    {
      path: "login/callback",
      element: <LoginCallbackV2Route />,
    },
    {
      path: "2fa",
      element: <TwoFactorV2Route />,
    },
    {
      path: "sign-up",
      element: <SignUpV2Route />,
    },
    {
      element: <AuthenticatedV2Route />,
      children: [
        {
          element: <AuthenticatedLayoutV2Route />,
          children: [
            {
              path: "home",
              element: <HomePageV2Route />,
            },
            {
              path: "visits",
              element: <VisitsV2Route />,
            },
            {
              path: "visit/new",
              element: <VisitV2Route />,
            },
            {
              path: "visit/:encounterId",
              element: <VisitV2Route />,
            },
            {
              path: "visits/:encounterId",
              element: <VisitV2Route />,
            },
            {
              path: "patients",
              element: <PatientsV2Route />,
            },
            {
              path: "patients/:patientId",
              element: <PatientDetailV2Route />,
            },
            {
              path: "patients/:patientId/visits",
              element: <PatientDetailV2Route />,
            },
            {
              path: "documents",
              element: <CareProtocolOpportunitiesV2Route />,
            },
            {
              path: "settings",
              element: <SettingsLayoutV2Route />,
              children: [
                {
                  index: true,
                  element: <SettingsIndexV2Route />,
                },
                {
                  path: "account",
                  element: <AccountSettingsV2Route />,
                },
                {
                  path: "preferences",
                  element: <PreferencesSettingsV2Route />,
                },
                {
                  path: "practice-info",
                  element: <PracticeInfoSettingsV2Route />,
                },
                {
                  path: "security",
                  element: <SecuritySettingsV2Route />,
                },
                {
                  path: "team",
                  element: <TeamSettingsV2Route />,
                },
              ],
            },
            {
              path: "not-found",
              element: <NotFoundV2Route />,
            },
          ],
        },
      ],
    },
  ];
}
